import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useCallback, useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { AuthContext } from '../../providers/AuthProvider';
import { PatternContext } from '../../providers/PatternProvider';
import { useGeneratePattern } from '../../hooks/useGeneratePattern';
import {
	IMAGETYPE,
	MEASUREMENT_UNIT,
	FREE_PRICE,
} from '../../config/constants';
import {
	getHeightCM,
	getHeightInchPrimary,
	getHeightInchFraction,
} from '../../utils/numbers';
import { COLOR_DIVIDER, COLOR_MENU_TEXT } from '../../styles/tokens';
import { SubscribedCheck } from '../Account/SubscribedCheck';
import { Purchase } from './Purchase';
import { OpenPatternButton } from './Shared/OpenPatternButton';
import { PdfModal } from './Shared/PdfModal';
import {
	stepContainerStyle,
	flexSpacerStyle,
	buttonTypographySx,
} from '../../styles/patterns';

export const PrintPattern = (props) => {
	const { onResetClick, onBackClick } = props;

	return (
		<>
			<div
				style={{
					...stepContainerStyle,
					justifyContent: 'center',
				}}
			>
				<div style={flexSpacerStyle} />
				<SubscribedCheck
					subscribed={
						<PrintPatternButton isSubscribed={true} {...props} />
					}
					notSubscribed={
						<PrintPatternButton isSubscribed={false} {...props} />
					}
				/>
				<div style={flexSpacerStyle} />
				<div style={{ display: 'flex', gap: '16px' }}>
					<Button
						variant="outlined"
						color="primary"
						onClick={onBackClick}
					>
						<Typography variant="h5" sx={buttonTypographySx}>
							Back
						</Typography>
					</Button>
					<Button
						variant="outlined"
						color="primary"
						onClick={onResetClick}
					>
						<Typography variant="h5" sx={buttonTypographySx}>
							Reset
						</Typography>
					</Button>
				</div>
			</div>
		</>
	);
};

const PrintPatternButton = (props) => {
	const { isSubscribed } = props;
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [url, setUrl] = useState(null);
	const { user } = useContext(AuthContext);
	const {
		GetSamplePattern,
		imagePrimary,
		imageSecondary,
		imageType: imageTypeObj,
		libraryPatternInfo: libraryPatternInfoObj,
		height,
		lastOddPage,
		angleLine,
		isInkSaver,
		measurementUnit,
		extraFrontPages,
		extraBackPages,
	} = useContext(PatternContext);
	const { dataURL: imagePrimaryDataURL, metadata: imagePrimaryMetadata } =
		imagePrimary;
	const { dataURL: imageSecondaryDataURL, metadata: imageSecondaryMetadata } =
		imageSecondary;
	const { value: imageType } = imageTypeObj;
	const { value: libraryPatternInfo } = libraryPatternInfoObj;

	const {
		generatePattern,
		patternURL,
		error: generateError,
	} = useGeneratePattern(user);

	const shouldGetFullPattern = useCallback(() => {
		const isLibraryPattern =
			!!imagePrimaryMetadata || !!imageSecondaryMetadata;
		const { priceId } = libraryPatternInfo || {};
		const isFreeLibraryPattern = !priceId || priceId === FREE_PRICE.id;
		const shouldGetFullPattern = isLibraryPattern
			? isFreeLibraryPattern
			: isSubscribed;
		return shouldGetFullPattern;
	}, [
		imagePrimaryMetadata,
		imageSecondaryMetadata,
		libraryPatternInfo,
		isSubscribed,
	]);

	useEffect(() => {
		const fetchPattern = async () => {
			if (shouldGetFullPattern()) {
				const isLibraryPattern =
					!!imagePrimaryMetadata || !!imageSecondaryMetadata;
				await generatePattern({
					type: isLibraryPattern ? 'library' : 'user',
					fileName: imagePrimaryMetadata ?? null,
					fileName2: imageSecondaryMetadata ?? null,
					imagePrimaryDataURL: isLibraryPattern
						? null
						: imagePrimaryDataURL,
					imageSecondaryDataURL: isLibraryPattern
						? null
						: imageSecondaryDataURL,
					bookParams: {
						height: height.value,
						lastOddPage: lastOddPage.value,
						angleLine: angleLine.value,
						isInkSaver: isInkSaver.value,
						measurementUnit: measurementUnit.value,
						extraFrontPages: extraFrontPages.value,
						extraBackPages: extraBackPages.value,
					},
				});
			} else {
				const { url } = await GetSamplePattern();
				setUrl(url);
				setLoading(false);
			}
		};
		fetchPattern();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldGetFullPattern]);

	useEffect(() => {
		if (patternURL) {
			setUrl(patternURL);
			setLoading(false);
		}
	}, [patternURL]);

	useEffect(() => {
		if (generateError) {
			setLoading(false);
		}
	}, [generateError]);

	const openPattern = (safeURL) => {
		if (isMobile) {
			const newWin = window.open(safeURL);
			if (
				!newWin ||
				newWin.closed ||
				typeof newWin.closed == 'undefined'
			) {
				alert('Please disable your popup blocker and try again.');
			}
		} else {
			setShowModal(true);
		}
	};

	const isLibraryPattern = !!(imagePrimaryMetadata || imageSecondaryMetadata);
	const patternName = libraryPatternInfo?.name;

	const onGetPatternClick = () => {
		openPattern(url);
	};

	const heightDisplay =
		measurementUnit.value === MEASUREMENT_UNIT.cm
			? `${getHeightCM(height.value)} cm`
			: (() => {
					const primary = getHeightInchPrimary(height.value);
					const fraction = getHeightInchFraction(height.value);
					return fraction.label === '-'
						? `${primary}"`
						: `${primary} ${fraction.label}"`;
				})();

	const patternDetails = [
		{ label: 'Height', value: heightDisplay },
		{ label: 'Last page', value: lastOddPage.value },
		{
			label: 'Type',
			value: imageType === IMAGETYPE.DOUBLE ? 'Lenticular' : 'Single',
		},
		{ label: 'Cut lines', value: angleLine.value },
		{ label: 'Ink saver', value: isInkSaver.value ? 'On' : 'Off' },
		...(Number(extraFrontPages.value) > 0
			? [{ label: 'Extra start pages', value: extraFrontPages.value }]
			: []),
		...(Number(extraBackPages.value) > 0
			? [{ label: 'Extra end pages', value: extraBackPages.value }]
			: []),
	];

	const patternPreview = (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: '10px',
			}}
		>
			<div style={{ display: 'flex', gap: '8px' }}>
				<img
					src={imagePrimaryDataURL}
					alt=""
					style={{
						maxHeight: '160px',
						maxWidth: '100%',
						borderRadius: '6px',
					}}
				/>
				{imageType === IMAGETYPE.DOUBLE && (
					<img
						src={imageSecondaryDataURL}
						alt=""
						style={{
							maxHeight: '160px',
							maxWidth: '100%',
							borderRadius: '6px',
						}}
					/>
				)}
			</div>
			{isLibraryPattern && patternName && (
				<Typography
					variant="h4"
					sx={{ fontWeight: 600, mt: '4px', textAlign: 'center' }}
				>
					{patternName
						.split(' ')
						.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
						.join(' ')}
				</Typography>
			)}
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					justifyContent: 'center',
					gap: '4px 20px',
					borderTop: `1px solid ${COLOR_DIVIDER}`,
					pt: 1.5,
					maxWidth: '380px',
					width: '100%',
				}}
			>
				{patternDetails.map(({ label, value }) => (
					<Box
						key={label}
						sx={{ textAlign: 'center', minWidth: '72px' }}
					>
						<Typography
							variant="caption"
							sx={{
								color: COLOR_MENU_TEXT,
								display: 'block',
								lineHeight: 1.2,
								fontSize: '0.7rem',
								textTransform: 'uppercase',
								letterSpacing: '0.04em',
							}}
						>
							{label}
						</Typography>
						<Typography
							variant="h6"
							sx={{ fontWeight: 600, lineHeight: 1.4 }}
						>
							{value}
						</Typography>
					</Box>
				))}
			</Box>
		</div>
	);

	const patternModal = (
		<PdfModal
			url={url}
			open={showModal}
			onClose={() => setShowModal(false)}
		/>
	);

	if (shouldGetFullPattern()) {
		return (
			<>
				{patternPreview}
				{generateError && (
					<Typography color="error" variant="h6" sx={{ mt: 1 }}>
						Failed to generate pattern. Please try again.
					</Typography>
				)}
				<OpenPatternButton
					loading={loading}
					onGetPatternClick={onGetPatternClick}
				/>
				{patternModal}
			</>
		);
	} else {
		return (
			<>
				{patternPreview}
				<Purchase onGetPatternClick={onGetPatternClick} />
				{patternModal}
			</>
		);
	}
};
